/**
 * FollowupActivityLog — expandable "Follow-Up Activity" panel inside the
 * Proposal builder (iter243). Lists every follow-up event for the
 * proposal: auto emails, manual mailto sends, and reply-detection
 * auto-pauses. Newest first, 5 shown by default.
 */
import { useState, useEffect } from "react";
import { ChevronDown, ChevronRight, Mail, MailCheck, User } from "lucide-react";
import { format } from "date-fns";
import { getProposalFollowupLog } from "@/lib/api";

const fmtWhen = (iso) => {
  try { return format(new Date(iso), "MMM d, yyyy h:mm a"); } catch { return String(iso).slice(0, 10); }
};

function entryView(e) {
  if (e.source === "reply_detected") {
    return {
      icon: <MailCheck className="w-3.5 h-3.5 text-emerald-600 shrink-0" />,
      label: `Customer replied (${e.from || "unknown"}) — auto follow-ups stopped`,
      chip: { text: "reply", cls: "bg-emerald-100 text-emerald-700" },
    };
  }
  if (e.source === "manual") {
    return {
      icon: <User className="w-3.5 h-3.5 text-blue-600 shrink-0" />,
      label: `Manual follow-up #${e.followup_number || "?"} sent from mail app${e.to ? ` to ${e.to}` : ""}`,
      chip: { text: "manual", cls: "bg-blue-100 text-blue-700" },
    };
  }
  const ok = e.status === "sent";
  return {
    icon: <Mail className={`w-3.5 h-3.5 shrink-0 ${ok ? "text-slate-500" : "text-amber-500"}`} />,
    label: `Auto follow-up #${e.followup_number || "?"}${e.to ? ` to ${e.to}` : ""}`,
    chip: ok
      ? { text: "sent", cls: "bg-emerald-100 text-emerald-700" }
      : { text: String(e.status || "").replace(/_/g, " "), cls: "bg-amber-100 text-amber-700" },
  };
}

export function FollowupActivityLog({ proposalId }) {
  const [entries, setEntries] = useState([]);
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (!proposalId) { setEntries([]); return; }
    getProposalFollowupLog(proposalId)
      .then(r => setEntries(r.data || []))
      .catch(() => setEntries([]));
  }, [proposalId]);

  if (!proposalId || entries.length === 0) return null;
  const visible = showAll ? entries : entries.slice(0, 5);

  return (
    <div className="border border-blue-200 bg-blue-50/40 rounded-lg" data-testid="followup-activity-log">
      <button
        type="button"
        className="w-full flex items-center gap-2 px-3 py-2 text-left"
        onClick={() => setOpen(o => !o)}
        data-testid="followup-activity-toggle"
      >
        {open ? <ChevronDown className="w-3.5 h-3.5 text-blue-500" /> : <ChevronRight className="w-3.5 h-3.5 text-blue-500" />}
        <Mail className="w-3.5 h-3.5 text-blue-600" />
        <span className="text-xs font-semibold text-blue-800">Follow-Up Activity</span>
        <span className="text-[10px] font-medium bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">{entries.length}</span>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-1.5">
          {visible.map((e, i) => {
            const v = entryView(e);
            return (
              // eslint-disable-next-line react/no-array-index-key -- read-only log
              <div key={`fa-${i}`} className="flex items-center gap-2 text-xs bg-white border border-blue-100 rounded px-2 py-1.5" data-testid={`followup-activity-${i}`}>
                {v.icon}
                <div className="flex-1 min-w-0">
                  <div className="text-slate-700 truncate">{v.label}</div>
                  <div className="text-[10px] text-slate-400">{fmtWhen(e.sent_at)}</div>
                </div>
                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded shrink-0 ${v.chip.cls}`}>{v.chip.text}</span>
              </div>
            );
          })}
          {entries.length > 5 && (
            <button
              type="button"
              className="text-[11px] text-blue-700 font-medium hover:underline"
              onClick={() => setShowAll(s => !s)}
              data-testid="followup-activity-show-all"
            >
              {showAll ? "Show less" : `Show all ${entries.length} events`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default FollowupActivityLog;
